import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useHaptics } from '../../utils/haptics'
import { usePublicSettings } from '../../hooks/usePublicSettings'
import { api } from '../../utils/accountApi'

// Icons
function SendIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 2L11 13"/>
      <path d="M22 2l-7 20-4-9-9-4z"/>
    </svg>
  )
}

function SupportIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10"/>
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
      <path d="M12 17h.01"/>
    </svg>
  )
}

function ArrowLeftIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  )
}

const THREAD_KEY = 'help_chat_thread'

export default function Chat() {
  const navigate = useNavigate()
  const haptics = useHaptics()
  const settings = usePublicSettings()
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [threadId, setThreadId] = useState(() => {
    try { return sessionStorage.getItem(THREAD_KEY) || null } catch { return null }
  })
  const { user } = useAuth()
  const messagesEndRef = useRef(null)
  const brand = settings?.['onboarding.brand_name']

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Answers come from the platform's AI assistant (POST /api/chat/message), the same engine as AI Inspect
  const handleSendMessage = async (e) => {
    e.preventDefault()
    const text = newMessage.trim()
    if (!text || loading) return
    haptics.light()
    setNewMessage('')
    setMessages(prev => [...prev, { id: `c${Date.now()}`, text, sender: 'customer', timestamp: new Date().toISOString(), senderName: user?.name || 'You' }])
    setLoading(true)
    try {
      const res = await api('/api/chat/message', { method: 'POST', body: { message: text, threadId } })
      if (res.threadId && res.threadId !== threadId) {
        setThreadId(res.threadId)
        try { sessionStorage.setItem(THREAD_KEY, res.threadId) } catch { /* private mode */ }
      }
      setMessages(prev => [...prev, { id: `a${Date.now()}`, text: res.message, sender: 'assistant', timestamp: new Date().toISOString(), senderName: 'Assistant' }])
    } catch (err) {
      setMessages(prev => [...prev, { id: `e${Date.now()}`, text: `Your message could not be sent: ${err.message}`, sender: 'system', timestamp: new Date().toISOString(), senderName: 'Error' }])
    } finally {
      setLoading(false)
    }
  }

  const formatTime = (timestamp) => new Date(timestamp).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

  const MessageBubble = ({ message }) => {
    const isCustomer = message.sender === 'customer'
    return (
      <div className={`flex ${isCustomer ? 'justify-end' : 'justify-start'} mb-4`}>
        <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl shadow-md ${
          isCustomer ? 'bg-slate-900 text-white' : message.sender === 'system' ? 'bg-red-50 text-red-700' : 'bg-white text-slate-900'
        }`}>
          {!isCustomer && (
            <div className="flex items-center mb-1">
              <SupportIcon className="w-4 h-4 mr-2 opacity-70" />
              <span className="text-xs font-medium opacity-70">{message.senderName}</span>
            </div>
          )}
          <p className="text-sm whitespace-pre-wrap">{message.text}</p>
          <p className={`text-xs mt-1 ${isCustomer ? 'text-slate-300' : 'text-slate-500'}`}>{formatTime(message.timestamp)}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-screen bg-white relative">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white px-4 py-4 shadow-lg">
        <div className="flex items-center gap-3">
          <button
            onClick={() => { haptics.light(); navigate(-1) }}
            className="p-2 hover:bg-slate-100 rounded-xl touch-manipulation"
          >
            <ArrowLeftIcon />
          </button>
          <SupportIcon className="w-6 h-6 text-slate-700" />
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Help &amp; Support</h1>
            <p className="text-sm text-slate-500">{brand ? `${brand} assistant` : 'Assistant'}</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 mobile-scroll bg-slate-50">
        <div className="text-xs text-slate-500 bg-white rounded-xl p-3 mb-4 shadow-sm">
          Replies here come from the AI assistant, which can answer questions about home repairs and help you book a professional.
          Chat with a human support agent is not available yet.
        </div>
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
        {loading && (
          <div className="flex justify-start mb-4">
            <div className="bg-slate-100 px-4 py-2 rounded-2xl">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="bg-white border-t border-slate-200 p-4 pb-20">
        <form onSubmit={handleSendMessage} className="flex items-center space-x-3">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 px-4 py-3 border border-slate-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
            disabled={loading}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || loading}
            className="p-3 bg-slate-900 text-white rounded-2xl hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
          >
            <SendIcon />
          </button>
        </form>
      </div>
    </div>
  )
}
